import React, {useEffect, useMemo, useState} from 'react';
import {CCol, CFormGroup, CInput, CInputCheckbox, CLabel, CRow} from '@coreui/react';
import useCustomForm from '../../../services/useCustomForm';
import Form from '../../../components/form';
import useCurrentAction from "../../../services/useCurrentAction";
import {getApi} from "../../../apis/backendApis";
import Permissoes, {ArvorePerm} from "./permissoes";

interface PerfilFormData {
    codigo: string,
    nome: string,
    descricao: string,
    ativo: boolean,
    concessoes: string[],
    usuarios?: number
}

/** Codigo sugerido a partir do nome: "Suporte N1" -> "suporte_n1". */
const codigoDoNome = (nome: string) => nome
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
    .slice(0, 60);

const PerfilForm = () => {
    const action = useCurrentAction();
    const [arvore, setArvore] = useState<ArvorePerm>({modulos: [], naoClassificadas: []});
    const [codigoEditado, setCodigoEditado] = useState(false);

    const initialValues: PerfilFormData = {
        codigo: '',
        nome: '',
        descricao: '',
        ativo: true,
        concessoes: [],
    };

    const {values, setValues, handleChange, handleSubmit, handleSubmitValues} = useCustomForm({
        initialValues,
        route: 'perfil-privilegio',
        onSubmit: form => handleSubmitValues({...form.values, concessoes: concessoesValidas(form.values.concessoes)}),
    });

    useEffect(() => {
        getApi().get('acesso/arvore').then(response => setArvore(response.data));
    }, []);

    const isView = () => action === 'view';
    const isEdit = () => action === 'edit';

    // Concessao que nao existe mais no mapa (programa removido, rota renomeada)
    // sai ao salvar, em vez de travar o formulario com erro.
    const chavesValidas = useMemo(() => {
        const chaves = new Set<string>();
        arvore.modulos.forEach(modulo => {
            chaves.add(`modulo:${modulo.id}`);
            modulo.programas.forEach(programa => {
                chaves.add(`programa:${programa.id}`);
                chaves.add(`consulta:${programa.id}`);
                programa.rotas.forEach(rota => chaves.add(`rota:${programa.id}:${rota.chave}`));
            });
        });
        arvore.naoClassificadas.forEach(rota => chaves.add(`rota:${rota.chave}`));
        return chaves;
    }, [arvore]);

    const concessoesValidas = (concessoes: string[]) => (concessoes || []).filter(chave => chavesValidas.has(chave));

    // Na inclusao, o codigo acompanha o nome ate a pessoa mexer nele.
    const mudarNome = (evento: React.ChangeEvent<HTMLInputElement>) => {
        const nome = evento.target.value;
        setValues({
            ...values,
            nome,
            codigo: !isEdit() && !codigoEditado ? codigoDoNome(nome) : values.codigo,
        });
    };

    const mudarCodigo = (evento: React.ChangeEvent<HTMLInputElement>) => {
        setCodigoEditado(true);
        setValues({...values, codigo: evento.target.value.toLowerCase()});
    };

    return (
        <Form entity="Perfil" handleSubmit={handleSubmit} className="perfil-form">
            {/* Dados do perfil numa linha so: a altura que sobra vai para as permissoes. */}
            <CRow className="perfil-dados">
                <CCol lg={3} md={6}>
                    <CFormGroup>
                        <CLabel className={'text-danger'} htmlFor="nome">Nome</CLabel>
                        <CInput id="nome" name="nome" value={values.nome} onChange={mudarNome}
                                readOnly={isView()} disabled={isView()} required placeholder="ex.: Suporte N1"/>
                    </CFormGroup>
                </CCol>
                <CCol lg={3} md={6}>
                    <CFormGroup>
                        <CLabel className={'text-danger'} htmlFor="codigo">Código</CLabel>
                        <CInput id="codigo" name="codigo" value={values.codigo} onChange={mudarCodigo}
                                readOnly={isView() || isEdit()} disabled={isView() || isEdit()} required
                                pattern="[a-z0-9_]{2,60}"
                                title="Identificador fixo: letras minúsculas, números e _. Não muda depois de criado."/>
                    </CFormGroup>
                </CCol>
                <CCol lg={4} md={8}>
                    <CFormGroup>
                        <CLabel htmlFor="descricao">Descrição</CLabel>
                        <CInput id="descricao" name="descricao" value={values.descricao || ''} onChange={handleChange}
                                readOnly={isView()} disabled={isView()} placeholder="Para quem é este perfil e o que ele permite"/>
                    </CFormGroup>
                </CCol>
                <CCol lg={2} md={4} className="d-flex align-items-end">
                    <CFormGroup className="perfil-ativo">
                        <CInputCheckbox id="ativo" name="ativo" checked={!!values.ativo} onChange={handleChange}
                                        readOnly={isView()} disabled={isView()}/>
                        <CLabel variant="checkbox" htmlFor="ativo">Ativo</CLabel>
                        {isEdit() && values.usuarios !== undefined && (
                            <div className="text-muted small">
                                {values.usuarios === 1 ? '1 usuário' : `${values.usuarios} usuários`}
                            </div>
                        )}
                    </CFormGroup>
                </CCol>
            </CRow>

            {arvore.modulos.length > 0 ? (
                <Permissoes
                    arvore={arvore}
                    concessoes={values.concessoes || []}
                    onChange={concessoes => setValues({...values, concessoes})}
                    somenteLeitura={isView()}
                />
            ) : (
                <div className="text-muted py-4">Carregando permissões…</div>
            )}
        </Form>
    );
};

export default PerfilForm;
